import { useEffect, useState, useCallback } from "react";
import ProGate from "@/components/ProGate";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, Cell, PieChart, Pie, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { BarChart3, Trophy, Skull, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import ComingUpWidget from "@/components/ComingUpWidget";
import { fmtUsd, fmtPct } from "@/lib/format";

function Big({ label, value, color = "" }) {
  return (
    <Card className="bg-[#111723] border-[#222F43] p-5">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono font-extrabold text-2xl sm:text-3xl mt-1 ${color}`}>{value}</div>
    </Card>
  );
}

function PortfolioInner() {
  const [stats, setStats] = useState(null);
  const [trades, setTrades] = useState([]);

  const load = useCallback(async () => {
    const [s, t] = await Promise.all([api.get("/trades/stats"), api.get("/trades")]);
    setStats(s.data); setTrades(t.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!stats) return null;
  const closed = trades.filter((t) => t.status === "closed");
  const barData = closed.slice(0, 15).reverse().map((t) => ({ name: t.ticker, pnl: t.pnl }));
  const pie = [
    { name: "Wins", value: stats.wins, color: "#00FF66" },
    { name: "Losses", value: stats.losses, color: "#FF3355" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
      <h1 className="font-display text-3xl font-bold flex items-center gap-2 mb-1"><BarChart3 className="h-7 w-7 text-emerald-400" /> Portfolio Stats</h1>
      <p className="text-slate-400 text-sm mb-6">Your degen performance, by the numbers.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="portfolio-stats">
        <Big label="Total P&L" value={fmtUsd(stats.totalPnl)} color={stats.totalPnl >= 0 ? "text-emerald-400 text-glow-green" : "text-red-400 text-glow-red"} />
        <Big label="Win Rate" value={`${stats.winRate}%`} color={stats.winRate >= 50 ? "text-emerald-400" : "text-amber-400"} />
        <Big label="Avg ROI" value={fmtPct(stats.avgRoi)} color={stats.avgRoi >= 0 ? "text-emerald-400" : "text-red-400"} />
        <Big label="Total Invested" value={fmtUsd(stats.invested)} />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
        <Card className="bg-[#111723] border-[#222F43] p-5 sm:p-6 lg:col-span-2">
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-4 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-cyan-400" /> P&L by Trade</h3>
          {barData.length === 0 ? <p className="text-slate-500 text-sm py-12 text-center">Close some trades to see your P&L chart.</p> : (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
                  <XAxis dataKey="name" stroke="#475569" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis stroke="#475569" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => fmtUsd(v)} />
                  <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#0d1420", border: "1px solid #334566", borderRadius: 8, fontSize: 12 }} formatter={(v) => [fmtUsd(v), "P&L"]} />
                  <Bar dataKey="pnl" radius={[4, 4, 0, 0]}>
                    {barData.map((d, i) => <Cell key={i} fill={d.pnl >= 0 ? "#00FF66" : "#FF3355"} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        <Card className="bg-[#111723] border-[#222F43] p-5 sm:p-6">
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-slate-400 mb-4">Win / Loss</h3>
          {stats.closedTrades === 0 ? <p className="text-slate-500 text-sm py-12 text-center">No closed trades yet.</p> : (
            <>
              <div className="h-44">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pie} dataKey="value" innerRadius={44} outerRadius={68} paddingAngle={3} stroke="none">
                      {pie.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0d1420", border: "1px solid #334566", borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-2 text-sm font-mono">
                <span className="text-emerald-400">● {stats.wins} wins</span>
                <span className="text-red-400">● {stats.losses} losses</span>
              </div>
            </>
          )}
        </Card>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-6">
        <Card className="bg-[#111723] border border-emerald-500/30 p-5 sm:p-6" data-testid="best-trade">
          <div className="flex items-center gap-2 mb-2"><Trophy className="h-5 w-5 text-emerald-400" /><span className="text-xs font-mono uppercase tracking-widest text-slate-400">Best Trade</span></div>
          {stats.bestTrade ? (
            <div className="flex items-baseline gap-3"><span className="font-display text-2xl font-bold">${stats.bestTrade.ticker}</span><span className="font-mono font-bold text-emerald-400 text-xl">{fmtUsd(stats.bestTrade.pnl)}</span><span className="font-mono text-emerald-400 text-sm">{fmtPct(stats.bestTrade.roiPct)}</span></div>
          ) : <p className="text-slate-500 text-sm">—</p>}
        </Card>
        <Card className="bg-[#111723] border border-red-500/30 p-5 sm:p-6" data-testid="worst-trade">
          <div className="flex items-center gap-2 mb-2"><Skull className="h-5 w-5 text-red-400" /><span className="text-xs font-mono uppercase tracking-widest text-slate-400">Worst Trade</span></div>
          {stats.worstTrade ? (
            <div className="flex items-baseline gap-3"><span className="font-display text-2xl font-bold">${stats.worstTrade.ticker}</span><span className="font-mono font-bold text-red-400 text-xl">{fmtUsd(stats.worstTrade.pnl)}</span><span className="font-mono text-red-400 text-sm">{fmtPct(stats.worstTrade.roiPct)}</span></div>
          ) : <p className="text-slate-500 text-sm">—</p>}
        </Card>
      </div>

      <ComingUpWidget />
    </div>
  );
}

export default function Portfolio() {
  return <ProGate feature="Portfolio"><PortfolioInner /></ProGate>;
}
